/**
 * 新素材未读提醒轮询模块（自包含，不依赖也不修改项目的请求封装）
 *
 * 行为：
 * 1. 已登录 + PC 端，每 60 秒静默请求一次 /adx/dxj/notify/unreadCount
 *    （独立 axios 实例，不经过 https2.js 拦截器：不弹全局 loading、失败不 toast）；
 * 2. 接口返回 data.tip 非空（有未读素材）时，在右上角用 ElNotification 弹出：
 *    title = "素材有更新"，message = data.tip，duration = 0（不自动关闭）；
 * 3. 通知内带「全部已读」按钮：点击后关闭通知，并 POST /adx/dxj/notify/readAll；
 * 4. 同一条 tip 不重复弹；tip 变化（又有新的未读素材）时更新为新的通知。
 *
 * 使用方式：在入口引入一次即可（被引入即自动启动，重复引入无副作用）
 *   import '@/utils/notifyPoll'
 */
import { h } from 'vue'
import axios from 'axios'
import { ElNotification, ElButton } from 'element-plus'
import { baseURL } from '@/request/config'
import router from '@/router/index.js'

// 接口地址
const UNREAD_COUNT_URL = '/adx/dxj/notify/unreadCount'
const READ_ALL_URL = '/adx/dxj/notify/readAll'
// 轮询间隔：1 分钟
const POLL_INTERVAL = 60 * 1000
// 接口没返回 tip 时的兜底文案
const DEFAULT_TIP = '有新的素材更新，请你去通知列表查看'

// 独立的静默请求实例（不挂任何全局拦截器）
const silentHttp = axios.create({
  baseURL,
  timeout: 15000,
})

silentHttp.interceptors.request.use((config) => {
  config.headers.authorize = 'Bearer' + localStorage.getItem('token')
  return config
})

silentHttp.interceptors.response.use(
  (response) => {
    if (response.data && response.data.code === 200) return response.data
    return Promise.reject(response.data)
  },
  (error) => Promise.reject(error)
)

let timer = null
let polling = false
// 当前打开的通知实例
let notificationInstance = null
// 当前通知展示的 tip，用于避免同一条未读重复弹
let shownTip = ''

const hasToken = () => Boolean(localStorage.getItem('token'))

// 只在 PC 端轮询：和 Home.vue 的布局断点保持一致（< 992px 视为移动端）
const mobileMedia = window.matchMedia('(max-width: 991.98px)')
const isDesktop = () => !mobileMedia.matches

// 解析未读数接口返回：data { playletCount, playletNames, tip }
const readPayload = (res) => {
  const data = res?.data
  if (typeof data === 'number') return { count: data, tip: '' }
  if (data && typeof data === 'object') {
    const num = Number(data.playletCount ?? data.count ?? data.unreadCount ?? data.unread ?? 0)
    return {
      count: Number.isNaN(num) ? 0 : num,
      tip: typeof data.tip === 'string' ? data.tip : '',
    }
  }
  return { count: 0, tip: '' }
}

// 关闭当前通知
const closeNotification = () => {
  if (notificationInstance) {
    notificationInstance.close()
    notificationInstance = null
  }
}

// 全部已读：关闭通知 + 调接口
const markAllRead = () => {
  closeNotification()
  shownTip = ''
  silentHttp.post(READ_ALL_URL).catch(() => { })
}

// 跳转到通知列表（通知保持打开，不关闭）
const goNotifyList = () => {
  // 已经在通知页时避免重复跳转
  if (router.currentRoute.value.path !== '/notify') {
    router.push('/notify')
  }
}

// 通知内容：tip 原文 +「全部已读」「去通知列表」按钮
const buildContent = (tip) =>
  h('div', null, [
    h('div', { style: 'line-height: 1.6; margin-bottom: 10px;' }, tip),
    h('div', { style: 'display: flex; align-items: center;' }, [
      h(
        ElButton,
        {
          type: 'primary',
          size: 'mini',
          onClick: () => markAllRead(),
        },
        () => '全部已读'
      ),
      h(
        ElButton,
        {
          size: 'mini',
          style: 'margin-left: 8px;',
          onClick: () => goNotifyList(),
        },
        () => '去通知列表'
      ),
    ]),
  ])

// 弹出通知（常驻，不自动关闭）
const showNotification = (tip) => {
  closeNotification()
  shownTip = tip
  notificationInstance = ElNotification({
    title: '素材有更新',
    message: buildContent(tip),
    type: 'warning',
    duration: 0,
    position: 'top-right',
    onClose: () => {
      notificationInstance = null
    },
  })
}

// 单次轮询
const pollOnce = () => {
  // 未登录或移动端不请求，并收起可能残留的通知
  if (!hasToken() || !isDesktop()) {
    closeNotification()
    shownTip = ''
    return Promise.resolve()
  }
  return silentHttp
    .get(UNREAD_COUNT_URL)
    .then((res) => {
      const { count, tip } = readPayload(res)
      // 没有未读：收起通知
      if (count <= 0) {
        closeNotification()
        shownTip = ''
        return
      }
      const message = tip || DEFAULT_TIP
      // 同一条未读不重复弹；tip 变化说明有新的未读素材，更新通知
      if (message === shownTip) return
      showNotification(message)
    })
    .catch(() => { })
}

const tick = () => {
  if (!timer || polling) return
  // 页面在后台时不请求，切回前台会补一次
  if (document.hidden) return
  polling = true
  pollOnce().finally(() => {
    polling = false
  })
}

const onVisibilityChange = () => {
  if (!document.hidden) tick()
}

// 视口跨过断点时：切回 PC 立即补一次
const onMobileMediaChange = () => {
  if (isDesktop() && hasToken()) tick()
}

// 启动轮询（重复调用无副作用）
export const startNotifyPoll = () => {
  if (timer) return
  pollOnce()
  timer = setInterval(tick, POLL_INTERVAL)
  document.addEventListener('visibilitychange', onVisibilityChange)
  if (mobileMedia.addEventListener) {
    mobileMedia.addEventListener('change', onMobileMediaChange)
  } else {
    mobileMedia.addListener(onMobileMediaChange)
  }
}

// 停止轮询（退出登录等场景可调用）
export const stopNotifyPoll = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (mobileMedia.removeEventListener) {
    mobileMedia.removeEventListener('change', onMobileMediaChange)
  } else {
    mobileMedia.removeListener(onMobileMediaChange)
  }
  closeNotification()
  shownTip = ''
  polling = false
}

// 被引入即自动启动：定时器常驻，未登录/移动端时 tick 内跳过
startNotifyPoll()
